import { useEffect, useState, useCallback } from "react";
import { api } from "../api/api";
import {
  Typography,
  Paper,
  Button,
  Box,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  MenuItem,
  Container
} from "@mui/material";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

// Icons
import DownloadIcon from "@mui/icons-material/Download";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import ShoppingBagIcon from "@mui/icons-material/ShoppingBag";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";

// ✅ Import the separated PDF generation logic
import { generatePdfReport } from "../pages/PdfReportButton";

// Modern input styling matching the application's aesthetic
const modernInputStyle = {
    '& .MuiInputLabel-root': {
        color: '#A59D96',
        fontWeight: '600',
    },
    '& .MuiInputLabel-root.Mui-focused': {
        color: '#D97706'
    },
    '& .MuiOutlinedInput-root': {
        borderRadius: '12px',
        bgcolor: '#FFFFFF',
        '& fieldset': {
            borderColor: '#ECE5DA',
        },
        '&:hover fieldset': {
            borderColor: '#F29D17',
        },
        '&.Mui-focused fieldset': {
            borderColor: '#F8C332',
            borderWidth: '2px'
        }
    },
    '& .MuiOutlinedInput-input': {
        fontWeight: '700',
        color: '#2F2A25',
    }
};

export default function Financial() {
  const vendor = JSON.parse(localStorage.getItem("vendor"));
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("today");

  const [showDatePicker, setShowDatePicker] = useState(false);
  
  // ✅ NEW STATE: Tracks whether the user wants a Range or a Single Date
  const [dateMode, setDateMode] = useState("range"); 

  const [customDates, setCustomDates] = useState({
    start: "",
    end: ""
  });

  // =========================
  // DOWNLOAD EXCEL
  // =========================
  const downloadExcel = () => {
    if (!data || !data.orders || data.orders.length === 0) {
      alert("No data available");
      return;
    }

    const groupedData = {};

    data.orders.forEach((order) => {
      const localDate = new Date(
        new Date(order.created_at).getTime()
        + (5.5 * 60 * 60 * 1000)
      );

      const dateKey = localDate.toLocaleDateString("en-GB");

      if (!groupedData[dateKey]) {
        groupedData[dateKey] = {
          rows: [],
          totalOrders: new Set(),
          totalVendorPrice: 0
        };
      }

      order.items.forEach((item) => {
        const totalVendorPrice = item.vendor_price * item.qty;

        groupedData[dateKey].rows.push({
          "Order ID": order.order_id,
          "Items": item.name,
          "Quantity": item.qty,
          "Vendor Price": item.vendor_price,
          "Total Vendor Price": totalVendorPrice,
          "Date": dateKey,
          "Time": localDate.toLocaleTimeString("en-IN")
        });

        groupedData[dateKey].totalOrders.add(order.order_id);
        groupedData[dateKey].totalVendorPrice += totalVendorPrice;
      });
    });

    const finalExcelData = [];

    Object.keys(groupedData).forEach((date) => {
      const group = groupedData[date];

      finalExcelData.push({ "Order ID": `DATE : ${date}` });
      finalExcelData.push({});
      finalExcelData.push({
        "Order ID": "Order ID",
        "Items": "Items",
        "Quantity": "Quantity",
        "Vendor Price": "Vendor Price",
        "Total Vendor Price": "Total Vendor Price",
        "Date": "Date",
        "Time": "Time"
      });

      group.rows.forEach((row) => {
        finalExcelData.push(row);
      });

      finalExcelData.push({});
      finalExcelData.push({ "Order ID": "SUMMARY" });
      finalExcelData.push({
        "Order ID": "Total Orders",
        "Items": group.totalOrders.size
      });

      finalExcelData.push({
        "Order ID": "Total Vendor Price",
        "Items": group.totalVendorPrice
      });

      finalExcelData.push({});
      finalExcelData.push({});
      finalExcelData.push({});
    });

    const worksheet = XLSX.utils.json_to_sheet(
      finalExcelData,
      { skipHeader: true }
    );

    worksheet["!cols"] = [
      { wch: 18 }, // Order ID
      { wch: 25 }, // Items
      { wch: 12 }, // Quantity
      { wch: 18 }, // Vendor Price
      { wch: 22 }, // Total Vendor Price
      { wch: 15 }, // Date
      { wch: 15 }  // Time
    ];

    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      "Financial Report"
    );

    const excelBuffer = XLSX.write(
      workbook,
      { bookType: "xlsx", type: "array" }
    );

    const fileData = new Blob(
      [excelBuffer],
      { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }
    );

    saveAs(
      fileData,
      `financial-${filter}.xlsx`
    );
  };

  // =========================
  // FETCH FINANCIAL DATA
  // =========================
  const fetchFinancial = useCallback(async (selectedFilter, start = "", end = "") => {
    try {
      let url = `/vendor/${vendor.id}/financial-summary?filter=${selectedFilter}`;
      
      if (selectedFilter === "custom" && start && end) {
        url += `&start_date=${start}&end_date=${end}`;
      }

      const res = await api.get(url);
      setData(res.data);
    } catch (err) {
      console.log(err);
    }
  }, [vendor.id]);

  useEffect(() => {
    if (filter === "custom" && (!customDates.start || !customDates.end)) {
      return; 
    }
    fetchFinancial(filter, customDates.start, customDates.end);
  }, [filter, customDates, fetchFinancial]); 

  const filterOptions = [
    { label: "Today", value: "today" },
    { label: "Week", value: "week" },
    { label: "Month", value: "month" },
    { label: "Year", value: "year" }
  ];

  // ✅ MODIFIED: Handles both Single Date and Date Range applications
  const handleApplyCustomDate = () => {
    if (dateMode === "single") {
      if (!customDates.start) {
        alert("Please select a date.");
        return;
      }
      // Make sure end date matches start date for single-day backend fetching
      setCustomDates({ start: customDates.start, end: customDates.start });
    } else {
      if (!customDates.start || !customDates.end) {
        alert("Please select both start and end dates.");
        return;
      }
    }
    
    setFilter("custom");
    setShowDatePicker(false);
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#FFFDF8", pt: { xs: 2, sm: 3 }, pb: 10, px: { xs: 2, sm: 3, md: 4 } }}>
      <Container maxWidth="md" disableGutters>
        
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h5" fontWeight="900" sx={{ color: "#3A2C23", letterSpacing: "-0.5px" }}>
            Financial Summary
          </Typography>
        </Box>

        {/* ========================= */}
        {/* FILTERS */}
        {/* ========================= */}
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1.5, mb: 4 }}>
          {filterOptions.map((opt) => (
            <Button
              key={opt.value}
              variant={filter === opt.value ? "contained" : "outlined"}
              disableElevation
              onClick={() => setFilter(opt.value)}
              sx={{
                borderRadius: "16px", 
                textTransform: "none",
                fontWeight: "800",
                px: 3,
                py: 1,
                bgcolor: filter === opt.value ? "#3A2C23" : "transparent",
                color: filter === opt.value ? "#FFFDF8" : "#6E655E",
                borderColor: filter === opt.value ? "#3A2C23" : "#ECE5DA",
                "&:hover": {
                  bgcolor: filter === opt.value ? "#2F2A25" : "#FFF6DE",
                  borderColor: filter === opt.value ? "#2F2A25" : "#F8C332",
                  color: filter === opt.value ? "#FFFDF8" : "#3A2C23" 
                }
              }}
            >
              {opt.label}
            </Button>
          ))}

          <Button
            variant={filter === "custom" ? "contained" : "outlined"}
            disableElevation
            onClick={() => setShowDatePicker(true)}
            sx={{
              borderRadius: "16px", 
              minWidth: "44px",
              width: "44px",
              height: "44px",
              p: 0,
              bgcolor: filter === "custom" ? "#3A2C23" : "transparent",
              color: filter === "custom" ? "#FFFDF8" : "#6E655E",
              borderColor: filter === "custom" ? "#3A2C23" : "#ECE5DA",
              "&:hover": {
                bgcolor: filter === "custom" ? "#2F2A25" : "#FFF6DE",
                borderColor: filter === "custom" ? "#2F2A25" : "#F8C332",
                color: filter === "custom" ? "#FFFDF8" : "#3A2C23" 
              }
            }}
          >
            <CalendarMonthIcon fontSize="small" />
          </Button>
        </Box>

        {/* ========================= */}
        {/* RESPONSIVE BUTTONS ROW */}
        {/* ========================= */}
        <Box 
          display="flex" 
          flexWrap="nowrap"
          gap={{ xs: 1.5, sm: 2 }}
          justifyContent="space-between" 
          mb={4}
          width="100%"
        >
          {/* EXCEL BUTTON */}
          <Button
            variant="contained"
            disableElevation
            startIcon={<DownloadIcon sx={{ fontSize: { xs: "1.2rem", sm: "1.5rem" } }} />}
            sx={{ 
              bgcolor: "#DDECC8", 
              color: "#577A34",
              borderRadius: "16px", 
              fontWeight: "900",
              textTransform: "none",
              px: { xs: 2, sm: 3 }, 
              py: { xs: 1.5, sm: 1.8 },
              fontSize: { xs: "0.85rem", sm: "1rem" }, 
              flex: 1, 
              lineHeight: 1.2,
              "&:hover": { bgcolor: "#CCE3AF" }
            }}
            onClick={downloadExcel}
          >
            Download Excel
          </Button>

          {/* PDF BUTTON */}
          <Button
            variant="contained"
            disableElevation
            startIcon={<PictureAsPdfIcon sx={{ fontSize: { xs: "1.2rem", sm: "1.5rem" } }} />}
            sx={{
              bgcolor: "#FFF0EE",
              color: "#E85D3F",
              borderRadius: "16px",
              fontWeight: "900",
              textTransform: "none",
              px: { xs: 2, sm: 3 }, 
              py: { xs: 1.5, sm: 1.8 },
              fontSize: { xs: "0.85rem", sm: "1rem" }, 
              flex: 1, 
              lineHeight: 1.2,
              "&:hover": { bgcolor: "#FFE4E0" }
            }}
            onClick={() => generatePdfReport(data, filter, customDates)} 
          >
            Download PDF
          </Button>
        </Box>

        {/* ========================= */}
        {/* MODERN SUMMARY CARDS */}
        {/* ========================= */}
        {data && (
          <Box 
            sx={{ 
              display: 'grid', 
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, 
              gap: 2.5 
            }}
          >
            {/* TOTAL REVENUE */}
            <Paper
              elevation={0}
              sx={{
                gridColumn: { xs: 'span 1', sm: 'span 2' },
                p: { xs: 3, sm: 4 },
                borderRadius: "24px",
                bgcolor: "#FFFFFF",
                border: "none",
                boxShadow: "0 10px 24px rgba(0, 0, 0, 0.12), 0 4px 8px rgba(0, 0, 0, 0.06)",
                display: "flex",
                alignItems: "center",
                gap: 2.5,
                transition: "transform 0.2s ease, box-shadow 0.2s ease",
                "&:hover": {
                  transform: "translateY(-3px)",
                  boxShadow: "0 16px 32px rgba(0, 0, 0, 0.15), 0 8px 16px rgba(0, 0, 0, 0.1)",
                }
              }}
            >
              <Avatar sx={{ bgcolor: "#FFF0EE", color: "#E85D3F", width: 64, height: 64 }}>
                <CurrencyRupeeIcon fontSize="large" />
              </Avatar>
              <Box>
                <Typography variant="caption" sx={{ color: '#A59D96', fontWeight: '800', letterSpacing: 0.5 }}>
                  TOTAL REVENUE
                </Typography>
                <Typography variant="h3" fontWeight="900" sx={{ color: "#3A2C23", mt: 0.5, lineHeight: 1.1 }}>
                  ₹{data.total_amount}
                </Typography>
              </Box>
            </Paper>

            {/* TOTAL ORDERS */}
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: "24px",
                bgcolor: "#FFFFFF",
                border: "none",
                boxShadow: "0 10px 24px rgba(0, 0, 0, 0.12), 0 4px 8px rgba(0, 0, 0, 0.06)",
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
                transition: "transform 0.2s ease, box-shadow 0.2s ease",
                "&:hover": {
                  transform: "translateY(-3px)",
                  boxShadow: "0 16px 32px rgba(0, 0, 0, 0.15), 0 8px 16px rgba(0, 0, 0, 0.1)",
                }
              }}
            >
              <Avatar sx={{ bgcolor: "#E1EED3", color: "#577A34", width: 48, height: 48 }}>
                <ReceiptLongIcon />
              </Avatar>
              <Box mt={1}>
                <Typography variant="caption" sx={{ color: '#A59D96', fontWeight: '800', letterSpacing: 0.5 }}>
                  TOTAL ORDERS
                </Typography>
                <Typography variant="h4" fontWeight="900" sx={{ color: "#3A2C23", mt: 0.5 }}>
                  {data.total_orders}
                </Typography>
              </Box>
            </Paper>

            {/* ITEMS SOLD */}
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: "24px",
                bgcolor: "#FFFFFF",
                border: "none",
                boxShadow: "0 10px 24px rgba(0, 0, 0, 0.12), 0 4px 8px rgba(0, 0, 0, 0.06)",
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
                transition: "transform 0.2s ease, box-shadow 0.2s ease",
                "&:hover": {
                  transform: "translateY(-3px)",
                  boxShadow: "0 16px 32px rgba(0, 0, 0, 0.15), 0 8px 16px rgba(0, 0, 0, 0.1)",
                }
              }}
            >
              <Avatar sx={{ bgcolor: "#FFF6DE", color: "#F29D17", width: 48, height: 48 }}>
                <ShoppingBagIcon />
              </Avatar>
              <Box mt={1}>
                <Typography variant="caption" sx={{ color: '#A59D96', fontWeight: '800', letterSpacing: 0.5 }}>
                  ITEMS SOLD
                </Typography>
                <Typography variant="h4" fontWeight="900" sx={{ color: "#3A2C23", mt: 0.5 }}>
                  {data.items_sold}
                </Typography>
              </Box>
            </Paper>
          </Box>
        )}

        {/* ========================= */}
        {/* ✅ UPDATED: CUSTOM DATE PICKER DIALOG */}
        {/* ========================= */}
        <Dialog 
          open={showDatePicker} 
          onClose={() => setShowDatePicker(false)}
          PaperProps={{ 
            sx: { 
              borderRadius: "24px", 
              p: 1, 
              bgcolor: '#FFFDF8',
              minWidth: { xs: "300px", sm: "400px" },
              boxShadow: '0 24px 48px rgba(0,0,0,0.2)' 
            } 
          }}
        >
          <DialogTitle sx={{ fontWeight: "900", pb: 2, pt: 3, px: { xs: 2, sm: 4 }, color: '#3A2C23', borderBottom: '1px dashed #ECE5DA' }}>
            Custom Date Filter
          </DialogTitle>
          <DialogContent sx={{ p: { xs: 2, sm: 4 }, pt: "24px !important" }}>
            <Stack spacing={3} mt={1}>
              
              {/* ✅ DROPDOWN TO SELECT MODE */}
              <TextField
                select
                fullWidth
                label="Filter Type"
                value={dateMode}
                sx={modernInputStyle}
                onChange={(e) => {
                  setDateMode(e.target.value);
                  // Reset dates when switching to prevent confusion
                  setCustomDates({ start: "", end: "" }); 
                }}
              >
                <MenuItem value="range" sx={{ fontWeight: "700", color: '#3A2C23' }}>Date Range</MenuItem>
                <MenuItem value="single" sx={{ fontWeight: "700", color: '#3A2C23' }}>Single Date</MenuItem>
              </TextField>

              {/* ✅ CONDITIONAL RENDER BASED ON DROPDOWN */}
              {dateMode === "single" ? (
                <TextField
                  label="Select Date"
                  type="date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={customDates.start}
                  onChange={(e) => setCustomDates({ start: e.target.value, end: e.target.value })}
                  sx={modernInputStyle}
                />
              ) : (
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <TextField
                    label="Start Date"
                    type="date"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={customDates.start}
                    onChange={(e) => setCustomDates({ ...customDates, start: e.target.value })}
                    sx={modernInputStyle}
                  />
                  <TextField
                    label="End Date"
                    type="date"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={customDates.end}
                    onChange={(e) => setCustomDates({ ...customDates, end: e.target.value })}
                    sx={modernInputStyle}
                  />
                </Stack>
              )}

            </Stack>
          </DialogContent>
          <DialogActions sx={{ p: { xs: 2, sm: 3 }, px: { sm: 4 }, borderTop: "1px solid #ECE5DA", bgcolor: '#FFFFFF', borderRadius: '0 0 24px 24px' }}>
            <Button 
              onClick={() => setShowDatePicker(false)} 
              color="inherit"
              sx={{ textTransform: "none", fontWeight: 800, color: '#6E655E', borderRadius: '12px', px: 3 }}
            >
              Cancel
            </Button>
            <Button 
              variant="contained" 
              disableElevation
              onClick={handleApplyCustomDate}
              sx={{ 
                textTransform: "none", 
                fontWeight: 900, 
                borderRadius: '12px', 
                bgcolor: '#F8C332', 
                color: '#3A2C23', 
                px: 4, 
                py: 1.2, 
                boxShadow: '0 4px 12px rgba(248, 195, 50, 0.3)',
                '&:hover': { bgcolor: '#F5B71D' } 
              }}
            >
              Apply Filter
            </Button>
          </DialogActions>
        </Dialog>

      </Container>
    </Box>
  );
}